import {
  useV2CreateChangeOrderMutation,
  useV2UpdateChangeOrderMutation,
  useV2DeleteChangeOrderMutation,
} from "@generated";
import type { V2ChangeOrderResource } from "@generated";
import type { ChangeOrder, ChangeOrderFormValues } from "../types";
import { toChangeOrder } from "./useChangeOrder";
import type { ChangeOrderBody } from "../builder/payload";

// The editable attributes; blank optional strings are sent as null so a user
// can clear a saved value.
const editableAttributes = (values: ChangeOrderFormValues) => ({
  cause: values.cause || null,
  "cause-note": values.causeNote || null,
  "external-reference": values.externalReference || null,
  "external-url": values.externalUrl || null,
});

// The editable attributes, plus hours and revenue on a reference-only change
// request; the generated type marks relationships required, hence the cast.
export const buildChangeOrderUpdate = (
  id: string,
  values: ChangeOrderFormValues
): V2ChangeOrderResource =>
  ({
    type: "change-orders",
    id,
    attributes: {
      ...editableAttributes(values),
      // Only a reference-only change request records its own totals.
      ...(values.referenceOnly
        ? { hours: values.hours.trim(), revenue: values.revenue.trim() }
        : {}),
    },
  } as unknown as V2ChangeOrderResource);

export const useChangeOrderMutations = () => {
  const [create] = useV2CreateChangeOrderMutation();
  const [update] = useV2UpdateChangeOrderMutation();
  const [destroy] = useV2DeleteChangeOrderMutation();

  return {
    createFromBody: async (body: ChangeOrderBody): Promise<ChangeOrder> => {
      const res = await create({ body: body as any }).unwrap();
      return toChangeOrder(res.data as V2ChangeOrderResource);
    },
    update: (id: string, values: ChangeOrderFormValues) =>
      update({
        id,
        body: { data: buildChangeOrderUpdate(id, values) },
      }).unwrap(),
    remove: (id: string) => destroy({ id }).unwrap(),
  };
};

export default useChangeOrderMutations;
